import React, { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Login as LoginIcon,
  FlashOn as FlashOnIcon,
  Public as PublicIcon,
  TextSnippet as TextSnippetIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  MailOutline as MailOutlineIcon,
  Flag as FlagIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import LanguageSelector from '../../../../components/LanguageSelector/LanguageSelector';
import httpClient from '../../../../utils/httpClient';
import icn1 from '../../../../assets/thumbnails-screenshots/immigration-canada/1-home.png';
import icn2 from '../../../../assets/thumbnails-screenshots/immigration-canada/2-features.png';
import icn3 from '../../../../assets/thumbnails-screenshots/immigration-canada/3-notificações.png';

type LandingPlan = {
  priceId: string;
  priceValueNormalized?: string | null;
  priceValueNormalizedAsComplementAprox?: string | null;
  dateCotation?: string | null;
  isProductionPlan?: boolean | null;
  plansSubscription: number;
  name?: string | null;
  description?: string | null;
  commercialName?: string | null;
  currency?: string | null;
  paymentFrequence?: string | null;
  trialConfigurations?: {
    enableTrial?: boolean | null;
    trialDays?: number | null;
    TrialDays?: number | null;
    trialEndDateFormatted?: string | null;
  } | null;
};

enum PlanPriceMarginBottom {
  WithApproxConversion = 1,
  WithoutApproxConversion = 4
}

enum CarouselIndexDelta {
  Step = 1
}

enum UiNumber {
  OneBasedIndexOffset = 1
}

const ImmigrationLanding: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  
  const plansSectionRef = useRef<HTMLDivElement>(null);

  const [planData, setPlanData] = React.useState<LandingPlan | null>(null);
  const [icnCarouselIndex, setIcnCarouselIndex] = React.useState(0);
  const [icnLightboxOpen, setIcnLightboxOpen] = React.useState(false);

  const icnScreens = [icn1, icn2, icn3];
  const showApproxConversion = i18n.language?.toLowerCase().startsWith('pt') && Boolean(planData?.priceValueNormalizedAsComplementAprox);

  React.useEffect(() => {
    const isPt = i18n.language?.toLowerCase().startsWith('pt');
    document.documentElement.lang = isPt ? 'pt-BR' : 'en';

    const title = isPt
      ? 'Imigra Pulse Canada | Notificações de sorteios com agilidade'
      : 'Imigra Pulse Canada | Fast immigration draw notifications';
    const description = isPt
      ? 'Receba alertas de novos draws e atualizações com contexto e agilidade. Ideal para consultorias de imigração ao Canadá.'
      : 'Get alerts for new draws and updates with context and speed. Built for Canadian immigration consultancies.';
    const canonical = `${window.location.origin}/imigra-pulse-canada`;
    const head = document.head;

    const upsertMeta = (selector: string, attrs: Record<string, string>, content: string) => {
      let el = head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const upsertLink = (selector: string, attrs: Record<string, string>, href: string) => {
      let el = head.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, 'index,follow');
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot' }, 'index,follow');
    upsertLink('link[rel="canonical"]', { rel: 'canonical' }, canonical);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
  }, [i18n.language]);

  React.useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await httpClient.get('/api/v1/portal/landing-page/list-available-plans', {
          headers: { 'X-Skip-Auth': 'true', 'X-Skip-Loading': 'true' }
        });
        if (res?.data?.success && Array.isArray(res.data.data)) {
          const standardPlan = res.data.data[0] as LandingPlan | undefined;
          if (standardPlan) {
            setPlanData(standardPlan);
          }
        }
      } catch {
      }
    };
    fetchPlans();
  }, []);

  const handleLoginClick = () => {
    const targetPath = isAuthenticated ? '/imigra-pulse-canada/painel' : '/login';
    navigate(targetPath);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleGoToHome = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleScrollToPlans = () => {
    if (plansSectionRef.current) {
      plansSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <FlashOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: t('imigracao_canada_diff_agility_title'),
      description: t('imigracao_canada_diff_agility_desc')
    },
    {
      icon: <PublicIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
      title: t('imigracao_canada_diff_coverage_title'),
      description: t('imigracao_canada_diff_coverage_desc')
    },
    {
      icon: <TextSnippetIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      title: t('imigracao_canada_diff_captions_title'),
      description: t('imigracao_canada_diff_captions_desc')
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
      title: t('imigracao_canada_diff_dashboards_title'),
      description: t('imigracao_canada_diff_dashboards_desc')
    }
  ];

  const coverageItems = [
    { name: t('imigracao_canada_coverage_ee'), color: '#d32f2f' },
    { name: t('imigracao_canada_coverage_mb'), color: '#1976d2' },
    { name: t('imigracao_canada_coverage_ab'), color: '#0288d1' },
    { name: t('imigracao_canada_coverage_on'), color: '#388e3c' },
    { name: t('imigracao_canada_coverage_bc'), color: '#f57c00' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                onClick={handleGoToHome}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGoToHome();
                  }
                }}
                role="link"
                tabIndex={0}
                sx={{ cursor: 'pointer' }}
              >
                WG Tech IT
              </Box>
              <Box component="span">| Imigra Pulse</Box>
              <span
                className="fi fi-ca"
                role="img"
                aria-label="Canadá"
                style={{ width: 20, height: 14, display: 'inline-block', borderRadius: 2 }}
              />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageSelector selectedColor={theme.palette.primary.main} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<LoginIcon />}
                onClick={handleLoginClick}
                sx={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                {isAuthenticated ? t('imigracao_canada_cta_access') : t('routes_login_label')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Abstract background shapes */}
        <Box sx={{ position: 'absolute', top: '-10%', right: '-5%', width: '40%', height: '100%', opacity: 0.1, background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 70%)' }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={8}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 3, lineHeight: 1.2 }}
              >
                {t('imigracao_canada_hero_headline')}
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}
              >
                {t('imigracao_canada_hero_subheadline')}
              </Typography>
              
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50' }} />
                <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.85 }}>
                  {t('imigracao_canada_hero_microtrust')}
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLoginClick}
                  sx={{
                    bgcolor: '#fff',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('imigracao_canada_cta_access')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleScrollToPlans}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
                  }}
                >
                  {t('imigracao_canada_cta_view_plan')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Diferenciais Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Grid container spacing={4} alignItems="stretch">
          {features.map((feature, index) => (
            <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={6} key={index} sx={{ display: 'flex', width: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  flex: 1,
                  width: '100%',
                  minWidth: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  {feature.icon}
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 0 }}>
                    {feature.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Como Funciona Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
            {t('imigracao_canada_how_it_works_title')}
          </Typography>
          
          <Grid container spacing={6} alignItems="stretch">
            <Grid component={'div' as React.ElementType} item xs={12} md={6} sx={{ minWidth: 0 }}>
              <List sx={{ '& .MuiListItem-root': { mb: 2 } }}>
                {[
                  t('imigracao_canada_how_it_works_step1'),
                  t('imigracao_canada_how_it_works_step2'),
                  t('imigracao_canada_how_it_works_step3')
                ].map((step, index) => (
                  <ListItem key={index} sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: 2 }}>
                    <ListItemIcon>
                      <Box sx={{ 
                        width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', 
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
                      }}>
                        {index + UiNumber.OneBasedIndexOffset}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={step}
                      sx={{ minWidth: 0 }}
                      primaryTypographyProps={{ fontWeight: 500, sx: { whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={6} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  p: { xs: 2, md: 4 },
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                  width: '100%',
                  maxWidth: { xs: 'none', md: 520 },
                  ml: { md: 'auto' }
                }}
              >
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  {t('imigracao_canada_how_it_works_context_desc')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Cobertura Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('imigracao_canada_coverage_title')}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {t('imigracao_canada_coverage_desc')}
          </Typography>
        </Box>
        
        <Grid container spacing={3} justifyContent="center">
          {coverageItems.map((item, index) => (
            <Grid component={'div' as React.ElementType} item key={index}>
              <Chip
                icon={<FlagIcon style={{ color: item.color }} />}
                label={item.name}
                sx={{ px: 2, py: 2.5, fontSize: '1rem', fontWeight: 500, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}
              />
            </Grid>
          ))}
          <Grid component={'div' as React.ElementType} item xs={12} textAlign="center" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              + {t('imigracao_canada_coverage_more')}
            </Typography>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('imigracao_canada_screens_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('imigracao_canada_screens_subtitle')}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: 2,
              overflow: 'hidden',
              width: '100%',
              maxWidth: 860,
              mx: 'auto',
              aspectRatio: '16 / 9',
              boxShadow: theme.shadows[4],
              position: 'relative'
            }}
          >
            <Box
              component="img"
              src={icnScreens[icnCarouselIndex]}
              alt={`ImigraPulse screenshot ${icnCarouselIndex + UiNumber.OneBasedIndexOffset}`}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                cursor: 'zoom-in',
                transform: 'scale(1.01)',
                transformOrigin: 'center'
              }}
              onClick={() => setIcnLightboxOpen(true)}
            />

            <Box sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  setIcnCarouselIndex((icnCarouselIndex + icnScreens.length - CarouselIndexDelta.Step) % icnScreens.length)
                }
                sx={{ minWidth: 0, p: 1 }}
                aria-label="Previous screenshot"
              >
                ‹
              </Button>
            </Box>
            <Box sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setIcnCarouselIndex((icnCarouselIndex + CarouselIndexDelta.Step) % icnScreens.length)}
                sx={{ minWidth: 0, p: 1 }}
                aria-label="Next screenshot"
              >
                ›
              </Button>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: 12,
                zIndex: 1
              }}
              aria-live="polite"
            >
              {`${icnCarouselIndex + UiNumber.OneBasedIndexOffset}/${icnScreens.length}`}
            </Box>
          </Box>

          <Dialog open={icnLightboxOpen} onClose={() => setIcnLightboxOpen(false)} maxWidth="lg" fullWidth>
            <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
              <IconButton
                onClick={() => setIcnLightboxOpen(false)}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'white' }}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={icnScreens[icnCarouselIndex]}
                alt={`ImigraPulse screenshot expanded ${icnCarouselIndex + UiNumber.OneBasedIndexOffset}`}
                sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', mx: 'auto' }}
              />
              <Box sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    setIcnCarouselIndex((icnCarouselIndex + icnScreens.length - CarouselIndexDelta.Step) % icnScreens.length)
                  }
                  sx={{ minWidth: 0, p: 1 }}
                  aria-label="Previous screenshot"
                >
                  ‹
                </Button>
              </Box>
              <Box sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIcnCarouselIndex((icnCarouselIndex + CarouselIndexDelta.Step) % icnScreens.length)}
                  sx={{ minWidth: 0, p: 1 }}
                  aria-label="Next screenshot"
                >
                  ›
                </Button>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: 12,
                  zIndex: 2
                }}
                aria-live="polite"
              >
                {`${icnCarouselIndex + UiNumber.OneBasedIndexOffset}/${icnScreens.length}`}
              </Box>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>

      {/* Legendas Personalizadas Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('imigracao_canada_captions_title')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                {t('imigracao_canada_captions_highlight')}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, mb: 4 }}>
                {t('imigracao_canada_captions_call')}
              </Typography>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper', mb: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                  TEMPLATE EDITOR (MOCK)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {['{nomeDraw}', '{dataPublicacao}', '{pontuacao}', '{dataCorte}'].map((tag) => (
                    <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
                <Box sx={{ minHeight: 120, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    🚀 {t('imigracao_canada_how_it_works_example_body')}
                  </Typography>
                </Box>
              </Paper>

              <Card elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: 'grey.200', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <MailOutlineIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('imigracao_canada_how_it_works_example_title')}
                  </Typography>
                </Box>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'grey.300', mb: 3 }}>
                    {'{nomeDraw}'} | {'{dataPublicacao}'} | {'{pontuacao}'} | {'{dataCorte}'}
                  </Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.primary', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2 }}>
                    "{t('imigracao_canada_how_it_works_example_body')}"
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Planos Section */}
      <Box ref={plansSectionRef} sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
            {t('imigracao_canada_plans_title')}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 3, boxShadow: 4, position: 'relative', overflow: 'visible' }}>
              <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}>
                <Chip label="Popular" color="primary" sx={{ fontWeight: 'bold', px: 1 }} />
              </Box>
              {planData?.isProductionPlan === false && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    px: 1.5,
                    py: 0.5,
                    bgcolor: '#ff9800',
                    color: '#fff',
                    borderRadius: 1,
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                  }}
                >
                  Sandbox
                </Box>
              )}
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
              {planData?.commercialName || t('imigracao_canada_plans_standard_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {planData?.description || t('imigracao_canada_plans_standard_desc')}
            </Typography>
            {(planData?.trialConfigurations?.enableTrial &&
              ((planData.trialConfigurations.trialDays ?? planData.trialConfigurations.TrialDays ?? 0) > 0)) && (
              <Typography variant="body2" color="success.main" sx={{ mb: 2, fontWeight: 600 }}>
                {t('subscription_plan_trial_days_label', {
                  days: planData.trialConfigurations.trialDays ?? planData.trialConfigurations.TrialDays
                })}
              </Typography>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                mb: showApproxConversion ? PlanPriceMarginBottom.WithApproxConversion : PlanPriceMarginBottom.WithoutApproxConversion,
                color: 'primary.main'
              }}
            >
              {planData?.priceValueNormalized || t('imigracao_canada_plans_standard_price')}
            </Typography>
            {showApproxConversion && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 4, fontStyle: 'italic' }}>
                {t('subscription_plan_approximate_conversion_text')} {planData?.priceValueNormalizedAsComplementAprox}
              </Typography>
            )}

                <List sx={{ mb: 3, textAlign: 'left' }}>
                  {[
                    t('imigracao_canada_plans_benefit_1'),
                    t('imigracao_canada_plans_benefit_2'),
                    t('imigracao_canada_plans_benefit_3')
                  ].map((benefit, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleLoginClick}
                  sx={{ py: 1.5, fontWeight: 'bold' }}
                >
                  {t('imigracao_canada_plans_subscribe')}
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Chamada Final (CTA) */}
      <Box sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            {t('imigracao_canada_final_cta_title')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={handleLoginClick}
              sx={{ fontWeight: 'bold', px: 4, py: 1.5 }}
            >
              {t('imigracao_canada_cta_access')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleScrollToPlans}
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              {t('imigracao_canada_cta_view_plan')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7 }}>
            {t('imigracao_canada_footer_copyright')}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default ImmigrationLanding;
